import mustache from "mustache";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { VentaAnimalesRequest } from "../../../../../modulos/pesaje/ganaderia/request/VentaAnimalesRequest";
import { toISODate } from "../../../../../utils/fechaUtils";
import { _2d, _miles } from "../../../../../utils/numberUtils";
import { IFormBodyRender } from "../../../../definitions/form/IFormBodyRender";
import { IFormField } from "../../../../definitions/form/IFormField";
import { InputDateField } from "../../../../implementations/form/date/InputDateField";
import { InputNumberField } from "../../../../implementations/form/number/InputNumberField";
import { SummaryItemRender } from "../../../../implementations/summary/SummaryItemRender";
import { LoteSelectorField } from "../../../terceros/lote/form/LoteSelectorField";
import template from "./ventaFormTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let VENTA_FORM_ID = 0;

/** Formulario de venta de animales (Render/Connect). La venta se arma con los
 *  pesos individuales de cada animal: al elegir el potrero de salida se muestran
 *  los animales disponibles y se habilita la cantidad a vender, que genera N
 *  inputs de peso; el server deriva cantidad/min/máx/promedio/total. Expone
 *  `validate()` para que el modal contenedor decida si guardar. */
export class VentaFormRender implements IFormBodyRender {
  /** Id único de la instancia (para los ids del DOM manuales). */
  private readonly _formId: number;
  private readonly _request: VentaAnimalesRequest;

  // === Componentes ===
  private readonly _lote: LoteSelectorField<VentaAnimalesRequest>;
  private readonly _fecha: InputDateField<VentaAnimalesRequest>;
  private readonly _animalesDisponibles: SummaryItemRender;
  private readonly _pesoMin: SummaryItemRender;
  private readonly _pesoProm: SummaryItemRender;
  private readonly _pesoMax: SummaryItemRender;
  private readonly _pesoTotal: SummaryItemRender;
  private readonly _precioKg: InputNumberField<VentaAnimalesRequest>;
  private readonly _valorTotal: SummaryItemRender;

  /** Campos con validación estándar (lote, fecha, precio). */
  private readonly _fields: IFormField[];
  /** Summaries de solo lectura (connect/dispose). */
  private readonly _summaries: SummaryItemRender[];

  /** Animales disponibles en el potrero elegido. */
  private _disponibles = 0;
  /** Inputs de peso individuales (uno por animal a vender). */
  private _pesoInputs: HTMLInputElement[] = [];
  /** Al editar: potrero original de la venta (sus animales ya salieron de ahí). */
  private readonly _loteOriginalId: string;
  /** Al editar: cantidad de animales de la venta original. */
  private readonly _cantidadOriginal: number;

  constructor(request: VentaAnimalesRequest) {
    VENTA_FORM_ID += 1;
    this._formId = VENTA_FORM_ID;
    this._request = request;
    // En edición, los animales de ESTA venta cuentan como disponibles en su
    // potrero original (el conteo actual del lote ya los descontó).
    this._loteOriginalId = request.id ? request.loteSalidaId : "";
    this._cantidadOriginal = request.id ? request.pesosAnimales.length : 0;

    this._lote = this._createLote();
    this._fecha = this._createFecha();
    this._animalesDisponibles = new SummaryItemRender({
      label: "Animales disponibles",
      description: "en el potrero de salida",
    });
    this._pesoMin = new SummaryItemRender({
      label: "Peso mínimo",
      description: "menor peso",
    });
    this._pesoProm = new SummaryItemRender({
      label: "Peso promedio",
      description: "promedio por animal",
    });
    this._pesoMax = new SummaryItemRender({
      label: "Peso máximo",
      description: "mayor peso",
    });
    this._pesoTotal = new SummaryItemRender({
      label: "Peso total",
      description: "suma de pesos",
    });
    this._precioKg = this._createPrecioKg();
    this._valorTotal = new SummaryItemRender({
      label: "Valor total",
      description: "peso total × precio kg",
    });

    this._fields = [this._lote, this._fecha, this._precioKg];
    this._summaries = [
      this._animalesDisponibles,
      this._pesoMin,
      this._pesoProm,
      this._pesoMax,
      this._pesoTotal,
      this._valorTotal,
    ];
  }

  render(): string {
    return mustache.render(template, {
      id: this._formId,
      lote: this._lote.render(),
      fecha: this._fecha.render(),
      animalesDisponibles: this._animalesDisponibles.render(),
      pesoMin: this._pesoMin.render(),
      pesoProm: this._pesoProm.render(),
      pesoMax: this._pesoMax.render(),
      pesoTotal: this._pesoTotal.render(),
      precioKg: this._precioKg.render(),
      valorTotal: this._valorTotal.render(),
    });
  }

  connect(): void {
    this._fields.forEach((field) => field.connect());
    this._summaries.forEach((summary) => summary.connect());

    this._cantidadInput = document.getElementById(
      `venta-cantidad-${this._formId}`,
    ) as HTMLInputElement | null;
    this._cantidadError = document.getElementById(
      `venta-cantidad-error-${this._formId}`,
    );
    this._pesosContainer = document.getElementById(`venta-pesos-${this._formId}`);
    this._pesosError = document.getElementById(`venta-pesos-error-${this._formId}`);

    this._cantidadInput?.addEventListener("input", this._onCantidadInput);
    this._precioKg.setOnChange(() => this._updateSummaries());

    this._updateSummaries();
  }

  dispose(): void {
    this._cantidadInput?.removeEventListener("input", this._onCantidadInput);
    this._pesoInputs.forEach((input) =>
      input.removeEventListener("input", this._onPesoInput),
    );
    this._fields.forEach((field) => field.dispose());
    this._summaries.forEach((summary) => summary.dispose());
  }

  /** Valida lote, fecha, cantidad ∈ [1, disponibles] y todos los pesos ≥ 0.1. */
  validate(): boolean {
    let isValid = true;
    this._fields.forEach((field) => {
      field.showError();
      if (field.getError()) {
        if (isValid) field.focus();
        isValid = false;
      }
    });

    const cantidad = this._getCantidad();
    if (cantidad < 1 || cantidad > this._disponibles) {
      this._setCantidadError(
        this._disponibles > 0
          ? `Ingrese entre 1 y ${this._disponibles}`
          : "Seleccione primero un potrero",
      );
      isValid = false;
    } else {
      this._setCantidadError("");
    }

    const pesosOk =
      this._pesoInputs.length > 0 &&
      this._pesoInputs.every((i) => this._parse(i.value) >= 0.1);
    if (!pesosOk) {
      this._setPesosError("Ingrese el peso de cada animal (mínimo 0.1 kg)");
      isValid = false;
    } else {
      this._setPesosError("");
    }

    return isValid;
  }

  private _cantidadInput: HTMLInputElement | null = null;
  private _cantidadError: HTMLElement | null = null;
  private _pesosContainer: HTMLElement | null = null;
  private _pesosError: HTMLElement | null = null;

  // ─── Potrero de salida ───

  /** Selector de potrero de salida, filtrado a potreros con animales. */
  private _createLote(): LoteSelectorField<VentaAnimalesRequest> {
    return new LoteSelectorField(
      this._request,
      {
        title: "Potrero de salida",
        get: (r) => r.loteSalidaId,
        set: (r, v) => {
          r.loteSalidaId = v;
        },
        // Seleccionables: potreros con animales; al editar, también el original
        // (pudo quedar en 0 justamente por esta venta).
        filter: (lote) =>
          lote.ganaderiaCantidadAnimales > 0 || lote.id === this._loteOriginalId,
        onSelect: (lote) => this._onLoteSelected(lote),
      },
      { isRequired: true },
    );
  }

  /** Al seleccionar el potrero: fija disponibles, habilita cantidad y sincroniza
   *  los pesos. En edición arranca con la cantidad ya vendida; si no, con todos. */
  private _onLoteSelected(lote: ClienteFincaLoteResponse): void {
    this._disponibles =
      lote.ganaderiaCantidadAnimales +
      (lote.id === this._loteOriginalId ? this._cantidadOriginal : 0);
    this._animalesDisponibles.setValue(String(this._disponibles));

    if (this._cantidadInput) {
      this._cantidadInput.disabled = false;
      this._cantidadInput.max = String(this._disponibles);
      const inicial =
        this._request.pesosAnimales.length > 0
          ? Math.min(this._request.pesosAnimales.length, this._disponibles)
          : this._disponibles;
      this._cantidadInput.value = String(inicial);
    }

    this._syncPesoRows(this._getCantidad());
  }

  // ─── Datos de la venta ───

  /** Fecha de venta (máximo hoy). */
  private _createFecha(): InputDateField<VentaAnimalesRequest> {
    return new InputDateField(this._request, {
      label: "Fecha de venta",
      description: "Seleccione la fecha de venta",
      max: toISODate(new Date()),
      get: (r) => r.fechaVenta,
      set: (r, v) => {
        r.fechaVenta = v;
      },
    });
  }

  /** Cantidad actual del input (clamp a [0, disponibles]). */
  private _getCantidad(): number {
    const raw = parseInt(this._cantidadInput?.value ?? "0", 10);
    if (isNaN(raw) || raw < 0) return 0;
    return Math.min(raw, this._disponibles);
  }

  /** Al cambiar la cantidad: clamp visual + re-sincroniza las filas de peso. */
  private _onCantidadInput = (): void => {
    const cantidad = this._getCantidad();
    if (this._cantidadInput && String(cantidad) !== this._cantidadInput.value) {
      this._cantidadInput.value = cantidad > 0 ? String(cantidad) : "";
    }
    this._syncPesoRows(cantidad);
    this._setCantidadError("");
  };

  // ─── Pesos de animales ───

  /** Ajusta el número de inputs de peso al `target` (agrega/quita filas). */
  private _syncPesoRows(target: number): void {
    if (!this._pesosContainer) return;
    const current = this._pesoInputs.length;

    if (target > current) {
      for (let i = current; i < target; i++) {
        const cell = document.createElement("div");
        cell.className = "venta-peso-cell";

        const label = document.createElement("label");
        label.className = "venta-peso-label";
        label.textContent = String(i + 1);

        const input = document.createElement("input");
        input.className = "sx-input sx-input--numeric";
        input.type = "number";
        input.min = "0.1";
        input.step = "0.1";
        const prefill = this._request.pesosAnimales[i];
        if (prefill != null && prefill > 0) input.value = String(prefill);
        input.addEventListener("input", this._onPesoInput);

        cell.appendChild(label);
        cell.appendChild(input);
        this._pesosContainer.appendChild(cell);
        this._pesoInputs.push(input);
      }
    } else if (target < current) {
      for (let i = current - 1; i >= target; i--) {
        this._pesoInputs[i].removeEventListener("input", this._onPesoInput);
        this._pesoInputs[i].parentElement?.remove();
        this._pesoInputs.pop();
      }
    }

    this._syncRequestPesos();
    this._updateSummaries();
  }

  /** Recalcula resúmenes y persiste los pesos al cambiar un input de peso. */
  private _onPesoInput = (): void => {
    this._syncRequestPesos();
    this._updateSummaries();
    this._setPesosError("");
  };

  /** Vuelca los pesos de los inputs al request (0 si vacío). */
  private _syncRequestPesos(): void {
    this._request.pesosAnimales = this._pesoInputs.map((i) =>
      this._parse(i.value),
    );
  }

  // ─── Resúmenes ───

  /** Recalcula peso mín/prom/máx, peso total y valor total (en vivo). */
  private _updateSummaries(): void {
    const values = this._pesoInputs
      .map((i) => this._parse(i.value))
      .filter((v) => v > 0);
    const total = values.reduce((s, v) => s + v, 0);
    const min = values.length > 0 ? Math.min(...values) : 0;
    const max = values.length > 0 ? Math.max(...values) : 0;
    const prom = values.length > 0 ? total / values.length : 0;
    const valor = total * this._request.precioKilo;

    this._pesoMin.setValue(min > 0 ? `${_2d(min)} kg` : null);
    this._pesoProm.setValue(prom > 0 ? `${_2d(prom)} kg` : null);
    this._pesoMax.setValue(max > 0 ? `${_2d(max)} kg` : null);
    this._pesoTotal.setValue(total > 0 ? `${_miles(total)} kg` : null);
    this._valorTotal.setValue(valor > 0 ? `$ ${_miles(valor)}` : null);
  }

  /** Precio por kilo (decimal, obligatorio, mínimo 1). */
  private _createPrecioKg(): InputNumberField<VentaAnimalesRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "Precio x kg ($)",
        description: "Precio de venta por kilo",
        decimal: true,
        get: (r) => r.precioKilo,
        set: (r, v) => {
          r.precioKilo = v;
        },
      },
      { isRequired: true, min: 1 },
    );
  }

  // ─── Helpers ───

  /** Parsea un valor de input a número decimal (0 si inválido). */
  private _parse(raw: string): number {
    const n = parseFloat(raw);
    return isNaN(n) ? 0 : n;
  }

  private _setCantidadError(msg: string): void {
    if (this._cantidadError) this._cantidadError.textContent = msg;
  }

  private _setPesosError(msg: string): void {
    if (this._pesosError) this._pesosError.textContent = msg;
  }
}
